import {
  CloseSquareOutlined,
  DeleteOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import CalendarIcon from "@untitled-ui/icons-react/build/esm/Calendar";
import ChevronDown from "@untitled-ui/icons-react/build/esm/ChevronDown";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Spin,
  Table,
  Typography,
} from "antd";
import dayjs from "dayjs";
import Notiflix from "notiflix";
import { useEffect, useState } from "react";
import { connect } from "react-redux";
import "../../../assets/styles/DatePicker.css";
import MainLayout from "../../../components/layouts/enhancement/MainLayout";
import TopContent from "../../../components/layouts/enhancement/TopContent";
import { rightAction } from "../../../redux/actions";
import cnt from "../../../services/definedConstant";
import { BOM } from "../../../services/definedMethod";
import { apiConfig, labelConfig, staticConfig } from "../teller.config";
import { showCheckerApprovalModal } from "../../../components/CheckerModal";
import { useNavigate } from "react-router-dom";

const { Text, Title } = Typography;

const getBatchStatusCode = (status) =>
  String(status ?? "")
    .trim()
    .charAt(0)
    .toUpperCase();

const isSameAsToday = (date) => {
  const parsedDate = dayjs(date);

  return parsedDate.isValid() && parsedDate.isSame(dayjs(), "day");
};

function VaultBatch(props) {
  const { userData } = props;
  const { session } = userData;
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [template, setTemplate] = useState([]);
  const [currencyDenominationOptions, setCurrencyDenominationOptions] =
    useState([]);
  const [denominationEntries, setDenominationEntries] = useState([]);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [batchUser, setBatchUser] = useState("");
  const [batchStatus, setBatchStatus] = useState("");
  const [balanceData, setBalanceData] = useState([]);
  const [transactionsData, setTransactionsData] = useState([]);
  const navigate = useNavigate();

  const balanceColumns = [
    { title: "Currency Code", dataIndex: "currency", key: "currency" },
    { title: "Balance", dataIndex: "balance", key: "balance" },
  ];

  const vaultTransactionColumns = [
    { title: "Date", dataIndex: "date", key: "date" },
    { title: "Currency Code", dataIndex: "currencyCode", key: "currencyCode" },
    {
      title: "Transaction Description",
      dataIndex: "transactionDescription",
      key: "transactionDescription",
    },
    {
      title: "Transaction Amount",
      dataIndex: "transactionAmount",
      key: "transactionAmount",
    },
    {
      title: "Transaction Status",
      dataIndex: "transactionStatus",
      key: "transactionStatus",
    },
  ];

  const denominationColumns = [
    { title: "Currency", dataIndex: "currency", key: "currency" },
    { title: "Denomination", dataIndex: "denomination", key: "denomination" },
    { title: "Count", dataIndex: "count", key: "count" },
    {
      title: "Value",
      dataIndex: "value",
      key: "value",
      render: (value) => BOM.FormatAmount(value),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button
          type="text"
          className="of-primaryBtnColor"
          icon={<DeleteOutlined />}
          onClick={() =>
            setDenominationEntries(
              denominationEntries.filter((e) => e.key !== record.key),
            )
          }
        />
      ),
    },
  ];

  const handleAddDenomination = () => {
    const values = form.getFieldsValue();
    const {
      currency,
      currencyDenomination,
      denominationCount,
      denominationValue,
    } = values;
    if (
      currency &&
      currencyDenomination &&
      denominationCount &&
      denominationValue
    ) {
      const newEntry = {
        key: Date.now(),
        currency: currencyOptions.find((c) => c.value === currency)?.label,
        denomination: currencyDenomination,
        count: denominationCount,
        value: denominationValue,
      };
      setDenominationEntries([...denominationEntries, newEntry]);
      form.setFieldsValue({
        currencyDenomination: undefined,
        denominationCount: undefined,
        denominationValue: undefined,
      });
    }
  };

  const getVaultCurrencyInfos = () =>
    denominationEntries.map((entry) => ({
      vaultCurrencyInfo: {
        locale: "en",
        currencyCode: entry.currency,
        denomCode: entry.denomination,
        denomCount: String(entry.count),
        denomValue: entry.value,
        denomName: entry.currency,
      },
    }));

  useEffect(() => {
    getTemplate();
  }, []);

  const getTemplate = () => {
    let body = {
      session,
    };

    const url = `${apiConfig.VaultBatch}/template?officeId=${userData?.officeId}`;

    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(cnt.DANGER, err?.errors[0]?.defaultUserMessage);
        setLoading(false);
      } else {
        const objList = res;
        let tempDenominationOptions = [];
        let tempCurrencyOptions = [];
        let tempBalanceData = [];
        let tempTransactions = [];

        objList?.denomCodes?.forEach((element) => {
          tempDenominationOptions.push({
            label: element,
            value: element,
          });
        });

        objList?.vaultCcyRestrConfData?.forEach((element) => {
          tempCurrencyOptions.push({
            label: element.currencyCode,
            value: element.id,
          });
        });

        Object.entries(objList?.vaultBalances ?? {}).forEach(
          ([currencyCode, balance]) => {
            tempBalanceData.push({
              key: currencyCode,
              currency: currencyCode,
              balance: BOM.FormatAmount(balance),
            });
          },
        );

        objList?.vaultBatchTransactions?.forEach((element, idx) => {
          tempTransactions.push({
            key: idx,
            date: dayjs(element?.transactionDate).format("DD MMM YYYY") ?? "-",
            currencyCode: element?.currencyCode ?? "-",
            transactionDescription:
              element?.txnType === "REQ"
                ? "Request Cash From Vault"
                : element?.txnType === "SND"
                  ? "Send Cash to Vault"
                  : element?.txnType === "WTH"
                    ? "Teller Cash Withdrawal"
                    : element?.txnType === "DEP"
                      ? "Teller Cash Deposit"
                      : element?.txnType === "CLO"
                        ? "Teller Batch Closure"
                        : "-",
            transactionAmount: element?.amount?.toLocaleString() ?? "-",
            currency: element?.currencyCode ?? "-",
            transactionStatus:
              element?.status === 2 ? "Received" : "Unprocessed",
          });
        });

        setTemplate(objList);
        setTransactionsData(tempTransactions);
        setCurrencyDenominationOptions(tempDenominationOptions);
        setCurrencyOptions(tempCurrencyOptions);
        setBalanceData(tempBalanceData);
        setBatchUser(objList?.vaultUser?.id);
        setBatchStatus(objList?.vaultId?.batchStatus ?? "C");

        form.setFieldsValue({
          branchCode:
            objList?.branchCode?.externalId ?? objList?.branchCode?.id,
          batchStatus: objList?.vaultId?.batchStatus,
          date:
            objList?.vaultId?.batchStatus === "O" &&
            objList?.vaultId?.businesDate
              ? dayjs(objList.vaultId?.businesDate)
              : null,
        });
        setLoading(false);
      }
    });
  };

  const onSubmit = (values) => {
    const isReopeningClosedBatchToday =
      values?.batchStatus === "O" &&
      getBatchStatusCode(template?.vaultId?.batchStatus) === "C" &&
      isSameAsToday(
        template?.vaultId?.businesDate ?? template?.vaultId?.businessDate,
      );

    const payload = {
      locale: "en",
      dateFormat: "dd MMMM yyyy",
      businesDate: BOM.DateTime(values.date, "only-date"),
      branchCode: template?.branchCode?.id,
      batchStatus: values?.batchStatus,
      batchUser: batchUser,
      vaultId: template?.vaultId?.id,
      vaultCurrencyInfos: getVaultCurrencyInfos(),
      method:
        values?.batchStatus === "O" && !isReopeningClosedBatchToday
          ? "POST"
          : "PUT",
      session,
    };

    const url = `${apiConfig.VaultBatch}?officeId=${userData?.officeId}`;

    setLoading(true);
    BOM.FetchReqAction(payload, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(cnt.DANGER, err?.errors[0]?.defaultUserMessage);
        setLoading(false);
      } else {
        BOM.AlertMsg(cnt.SUCCESS, "Vault batch updated successfully");
        setDenominationEntries([]);
        getTemplate();
        setLoading(false);
        form.resetFields();
      }
    });
  };

  const handleDenominationChange = (value) => {
    const count = form.getFieldValue("denominationCount");
    if (value && count) {
      form.setFieldValue("denominationValue", value * count);
    }
  };

  const handleCountChange = (value) => {
    const denom = form.getFieldValue("currencyDenomination");
    if (denom && value) {
      form.setFieldValue("denominationValue", denom * value);
    }
  };

  const hasDenominationEntries = () => {
    if (denominationEntries.length > 0) return true;

    BOM.AlertMsg(
      cnt.WARNING,
      "Please add at least one currency denomination entry",
    );
    return false;
  };

  const handleOpenBatch = () => {
    if (!hasDenominationEntries()) return;

    let action = "Open  ";
    let label = "Vault Batch";

    form.validateFields(["date"]).then(() => {
      Notiflix.Confirm.show(
        "Are you sure?",
        action + label,
        "Yes",
        "No",
        () => {
          onSubmit({
            ...form.getFieldsValue(),
            batchStatus: "O",
          });
        },
      );
    });
  };

  const handleCloseBatch = () => {
    if (!hasDenominationEntries()) return;

    let action = "Close  ";
    let label = "Vault Batch";

    form.validateFields(["date"]).then(() => {
      Notiflix.Confirm.show(
        "Are you sure?",
        action + label,
        "Yes",
        "No",
        () => {
          onSubmit({
            ...form.getFieldsValue(),
            batchStatus: "C",
          });
        },
      );
    });
  };

  return (
    <MainLayout
      isEnableRightSidebar={false}
      pageItems={staticConfig.vaultBatchItems}
    >
      <TopContent
        pageTitle={labelConfig.manageVaultBatch}
        //  createPageTitle={labelConfig.createMoneyMarketBond}
      />
      <Card
        style={{
          boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
          borderRadius: 20,
          marginTop: 20,
        }}
      >
        <Spin spinning={loading}>
          <Form
            form={form}
            layout="vertical"
            // onFinish={onSubmit}
          >
            <Row justify="end" style={{ marginBottom: 12 }}>
              <Button
                className="of-primaryBtnColor"
                style={{
                  width: "10%",
                }}
                icon={<UnorderedListOutlined />}
                htmlType="button"
                disabled={batchStatus === "O"}
                onClick={handleOpenBatch}
              >
                Open Vault
              </Button>
              <Button
                className="of-primaryBtnColor"
                style={{
                  width: "10%",
                  marginLeft: 10,
                }}
                shape="default"
                icon={<CloseSquareOutlined />}
                htmlType="button"
                disabled={batchStatus === "C"}
                onClick={handleCloseBatch}
              >
                Close Vault
              </Button>
            </Row>
            <Row>
              <Col span={12}>
                <Form.Item name="date" label={<Text strong>Date</Text>}>
                  <DatePicker
                    style={{ marginBottom: 0, width: "100%" }}
                    placeholder="Select date"
                    suffixIcon={<CalendarIcon color="#667085" width={20} />}
                    className="bond-date-picker custom-input"
                    disabled={batchStatus === "O"}
                  />
                </Form.Item>
                <Form.Item
                  name="branchCode"
                  label={<Text strong>Branch Code</Text>}
                  // rules={[
                  //   {
                  //     required: true,
                  //     message: "Required Field",
                  //   },
                  // ]}
                >
                  <Input
                    className="of-bold-disable-input"
                    disabled
                    style={{ width: "100%", height: "44px" }}
                  />
                </Form.Item>
              </Col>
            </Row>

            {batchStatus === "O" && (
              <>
                <Divider />
                <Title level={5}>Balance(s)</Title>
                <Table
                  dataSource={balanceData}
                  columns={balanceColumns}
                  pagination={false}
                  size="small"
                />
              </>
            )}

            <Divider style={{ marginTop: 40 }} />
            <Title level={5}>Currency Denomination Info</Title>
            <Row style={{ marginBottom: 12 }} gutter={24}>
              <Col span={12}>
                <Form.Item
                  name="currency"
                  label={<Text strong>Currency</Text>}
                  // rules={[
                  //   {
                  //     required: true,
                  //     message: "Currency is required",
                  //   },
                  // ]}
                >
                  <Select
                    placeholder="Select currency"
                    style={{ height: 44 }}
                    suffixIcon={<ChevronDown color="#667085" width={20} />}
                    options={currencyOptions}
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  name="currencyDenomination"
                  label={<Text strong>Currency Denomination</Text>}
                  // rules={[
                  //   {
                  //     required: true,
                  //     message: "Currency denomination is required",
                  //   },
                  // ]}
                >
                  <Select
                    placeholder="Select denomination"
                    style={{ height: 44 }}
                    suffixIcon={<ChevronDown color="#667085" width={20} />}
                    options={currencyDenominationOptions}
                    onChange={handleDenominationChange}
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  name="denominationCount"
                  label={<Text strong>Denomination Count</Text>}
                  // rules={[
                  //   {
                  //     required: true,
                  //     message: "Denomination count is required",
                  //   },
                  // ]}
                >
                  <InputNumber
                    style={{ height: 44, width: "100%" }}
                    placeholder="0"
                    onChange={handleCountChange}
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  name="denominationValue"
                  label={<Text strong>Denomination Value</Text>}
                >
                  <InputNumber
                    style={{ height: 44, width: "100%" }}
                    placeholder="0"
                    className="of-bold-disable-input"
                    formatter={BOM.FormatAmount}
                    parser={BOM.ParseAmount}
                    disabled
                  />
                </Form.Item>
              </Col>

              <Form.Item>
                <Button
                  type="primary"
                  className="of-primaryBtnColor"
                  onClick={handleAddDenomination}
                  style={{ margin: "0px 14px" }}
                >
                  Add
                </Button>
              </Form.Item>
            </Row>

            {denominationEntries.length > 0 && (
              <Table
                style={{ marginTop: 16, marginBottom: 24 }}
                dataSource={denominationEntries}
                columns={denominationColumns}
                pagination={false}
                size="small"
              />
            )}

            {batchStatus === "O" && (
              <>
                <Divider />
                <Title level={5}>Vault Transaction(s)</Title>
                <Table
                  style={{ marginBottom: 24 }}
                  dataSource={transactionsData}
                  columns={vaultTransactionColumns}
                  // pagination={false}
                  pagination={{
                    position: ["bottomCenter"],
                    pageSize: 10,
                  }}
                  size="small"
                />
              </>
            )}
          </Form>
        </Spin>
      </Card>
    </MainLayout>
  );
}

const mapStateToProps = (state) => ({
  userData: state.AuthenticationData.userData,
  rightBtn: state.RightSideBtn,
});

const mapDispatchToProps = { rightAction };
export default connect(mapStateToProps, mapDispatchToProps)(VaultBatch);
